(ns dashboard.entity.document.db
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require
   [dashboard.entity.analysis.db :as analysis.db]
   [dashboard.entity.document.js-dom :as js.dom]
   [dashboard.notifications :as notifications]
   [dashboard.services.http-config :as http-config]
   [re-frame.core :as rf]))

;; ----------------------------------------------------------------------------
;;  Subscriptions
;; ----------------------------------------------------------------------------

(rf/reg-event-db
 ::fetch-document-paragraphs-success
 (fn [db [_ document-id callback paragraphs]]
    (callback)
   (assoc-in db
             [:document-paragraphs document-id]
             paragraphs)))

(rf/reg-event-fx
 ::fetch-document-paragraphs
 (fn [{:keys [db]} [_ document-id threshold callback]]
   {:http-xhrio {:method          :get
                 :uri             (str "/api/policies/paragraphs/"
                                       document-id)
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :url-params      {:threshold (- 1 (/ threshold 100))}
                 :response-format http-config/transit-response-format
                 :on-success      [::fetch-document-paragraphs-success
                                   document-id
                                   callback]
                 :on-failure      [::notifications/add
                                   {:message "Could not fetch document paragraphs"
                                    :class "is-danger"}]}}))
(rf/reg-event-db
 ::fetch-document-record-success
 (fn [db [_ document-id data]]
   (assoc-in db [:document-record document-id] data)))

(rf/reg-event-fx
 ::fetch-document-record
 (fn [{:keys [db]} [_ document-id]]
   {:http-xhrio {:method          :get
                 :uri             (str "/api/policies/document/"
                                       document-id)
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format

                 :response-format http-config/transit-response-format
                 :on-success      [::fetch-document-record-success
                                   document-id]
                 :on-failure      [::notifications/add
                                   {:message "Could not fetch document record data"
                                    :class "is-danger"}]}}))

(rf/reg-event-db
 ::fetch-document-attribution-success
 (fn [db [_ document-id attribution]]
   (assoc-in db
             [:document-attribution document-id]
             attribution)))

(rf/reg-event-fx
 ::fetch-document-attribution
 (fn [{:keys [db]} [_ document-id]]
   {:http-xhrio {:method          :get
                 :uri             (str "/api/policies/attribution/"
                                       document-id)
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::fetch-document-attribution-success
                                   document-id]
                 :on-failure      [::notifications/add
                                   {:message "Configure document attribution before adding evidence"
                                    :class "is-info"}]}}))

(defn fetch-document-attribution
  [document-id]
  (rf/dispatch [::fetch-document-attribution document-id]))

(defn fetch-document-record
  [document-id]
  (rf/dispatch [::fetch-document-record document-id]))

(rf/reg-sub
 ::document-record
 (fn [db [_ document-id]]
   (get-in db [:document-record document-id])))

(rf/reg-sub
 ::attribution
 (fn [db [_ document-id]]
   (get-in db [:document-attribution document-id])))

(rf/reg-sub
 ::paragraphs
 (fn [db [_ document-id]]
   (get-in db [:document-paragraphs document-id])))

(rf/reg-sub
 ::selected-paragraph
 (fn [db _]
   (get-in db [:document-search :selected-paragraph])))

(rf/reg-sub
 ::document-search-results
 (fn [db _]
   (get-in db [:document-search :document-search-results])))

(rf/reg-event-db
 ::set-document-search-results
 (fn [db [_ paragraphs]]
   (assoc-in db [:document-search :document-search-results] paragraphs)))

(rf/reg-event-db
 ::set-selected-paragraph
 (fn [db [_ paragraphs]]
   (assoc-in db [:document-search :selected-paragraph] paragraphs)))

;; LFA - REMOVE THIS
(rf/reg-event-db
 ::set-error
 (fn [db [_ error e]]
   (assoc-in db [:ui :error] (if-let [message (or (-> error :response :message)
                                                  (:message error)
                                                  (:status-text error))]
                               message
                               error))))

(rf/reg-event-fx
 ::update-document-attribution
 (fn [_ [_ document-id attribution]]
   {:http-xhrio {:method :post
                 :uri (str "/api/policies/attribution/"
                           document-id)
                 :params (assoc attribution :document-id document-id)
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success [::fetch-document-attribution-success document-id attribution]
                 ;; LFA : Should be ui/set-error
                 :on-failure [::set-error "Failed to updated status of document attribution"]}}))

(rf/reg-event-db
 ::create-document-evidence-success
 (fn [db [_ document-id type callback evidence]]
   (callback)
   (analysis.db/get-applied-analyses false)
   (cond-> db
     true
     (update-in
      [:document-evidence document-id]
      (fn [coll] (conj (vec coll) evidence)))

     (= type :paragraph)
     ((fn [db]
        (let [paragraph-id (-> evidence :content-data :document-paragraph-id)
              paragraph (-> (get-in db [:document-paragraphs document-id])
                            (->> (filter (comp (partial = paragraph-id) str :id)))
                            first)
              construct-ids (-> evidence :construct-ids set)
              new (update paragraph
                          :analysis
                          #(mapv
                            (fn [{:keys [linked construct_id] :as asa}]
                              (assoc asa :linked (or linked (boolean (construct-ids construct_id)))))
                            %))]
          (assoc-in db [:document-paragraphs document-id]
                    (-> (get-in db [:document-paragraphs document-id])
                        (->> (remove (comp (partial = paragraph-id) str :id)))
                        vec
                        (conj new)))))))))

(rf/reg-event-fx
 ::create-document-evidence
 (fn [{:keys [db]} [_ document-id evidence construct-ids type callback]]
   {:http-xhrio {:method          :post
                 :uri             (str "/api/policies/evidence/"
                                       document-id)
                 :params          {:evidence-data evidence
                                   :selected-construct-ids construct-ids}
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::create-document-evidence-success
                                   document-id type callback]
                 :on-failure      [::notifications/add
                                   {:message (str "Could not create evidence" document-id)
                                    :class "is-danger"}]}}))

(rf/reg-event-db
 ::add-analysis-to-document-evidence-success
 (fn [db [_ document-id callback evidence]]
   (callback)
   (analysis.db/get-applied-analyses false)
   (let [evidence-id (-> evidence :evidence-id)
         evidence (-> (get-in db [:document-evidence document-id])
                      (->> (filter (comp (partial = evidence-id) :evidence-id)))
                      first)]
     (assoc-in db [:document-paragraphs document-id]
               (-> (get-in db [:document-paragraphs document-id])
                   (->> (remove (comp (partial = evidence-id) :evidence-id)))
                   vec
                   (conj evidence))))))

(rf/reg-event-fx
 ::add-analysis-to-document-evidence
 (fn [{:keys [db]} [_ document-id evidence construct-ids callback]]
   {:http-xhrio {:method          :post
                 :uri             (str "/api/policies/evidence/add-analysis/"
                                       (:evidence-id evidence))
                 :params          {:evidence-data (dissoc evidence :construct-ids)
                                   :document-id document-id
                                   :selected-construct-ids construct-ids}
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::add-analysis-to-document-evidence-success
                                   document-id callback]
                 :on-failure      [::set-error
                                   #_{:message (str "Could not link analysis to evidence "
                                                    (:evidence-id evidence))
                                      :class "is-danger"}]}}))

(rf/reg-event-db
 ::fetch-document-evidence-success
 (fn [db [_ document-id evidence]]
   (assoc-in db
             [:document-evidence document-id]
             evidence)))

(rf/reg-event-fx
 ::fetch-document-evidence
 (fn [{:keys [db]} [_ document-id]]
   {:http-xhrio {:method          :get
                 :uri             (str "/api/policies/evidence/"
                                       document-id)
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::fetch-document-evidence-success
                                   document-id]
                 :on-failure      [::notifications/add
                                   {:message (str "Could not fetch document evidence "
                                                  document-id)
                                    :class "is-danger"}]}}))

(defn fetch-document-evidence
  [document-id]
  (rf/dispatch [::fetch-document-evidence document-id]))

(rf/reg-sub
 ::evidence
 (fn [db [_ document-id]]
   (get-in db [:document-evidence document-id])))

(defn fetch-document-paragraphs
  [document-id threshold callback]
  (rf/dispatch [::fetch-document-paragraphs document-id threshold callback]))

(defn document-evidence-data*
  [document-id]
  (rf/subscribe [::evidence document-id]))

(defn document-record*
  [document-id]
  (rf/subscribe [::document-record document-id]))

(defn paragraphs*
  [document-id]
  (rf/subscribe [::paragraphs document-id]))

(defn document-attribution-data*
  [document-id]
  (rf/subscribe [::attribution document-id]))

(defn selected-paragraph-sub*
  []
  (rf/subscribe [::selected-paragraph]))

(defn document-search-results*
  []
  (rf/subscribe [::document-search-results]))

(defn set-selected-paragraph
  [paragraph]
  (rf/dispatch [::set-selected-paragraph paragraph]))

(defn set-document-search-results
  [paragraph]
  (rf/dispatch [::set-document-search-results paragraph]))

(defn set-attribution
  [document-id attribution]
  (rf/dispatch [::update-document-attribution document-id attribution]))

;; ----------------------------------------------------------------------------
;;  Complex Setters
;; ----------------------------------------------------------------------------

(defn on-create-document-evidence-from-selection
  [document-id search-query attribution-props selection-range selected-constructs callback]
  (when (seq search-query)
    (rf/dispatch
     [::create-document-evidence
      document-id
      (-> attribution-props
          :state-evidence
          (dissoc :evidence-id)
          (assoc-in [:content]
                    search-query)
          (assoc-in [:content-data]
                    {:type :pdfjs-selection
                     :html-ranges (js.dom/extract-spans-from-html
                                   (:html selection-range))
                     :page-range {:start (or (:start selection-range) 0)
                                  :end (or (:end selection-range) 0)}}))
      (mapv :construct-id selected-constructs)
      :selection
      callback])))

(defn on-create-document-evidence-from-paragraph
  [document-id
   attribution-props
   page
   document-paragraph-id
   paragraph-text
   html
   selected-constructs
   callback]
  (when (and (seq paragraph-text) html)
    (let [temp-div (js/document.createElement "div")
          _ (.appendChild temp-div (.cloneNode html true))
          temp-div (.-firstChild temp-div)
          _ (.removeAttribute temp-div "id")
          nodeclasses (.-classList temp-div)
          _ (.remove nodeclasses "paragraph")
          _ (.remove nodeclasses "document-viewer--paragraph")
          _ (.remove nodeclasses "highlight-info--active-highlight")
          _ (.add nodeclasses "document-viewer--evidence")]
      (rf/dispatch
       [::create-document-evidence
        document-id
        (-> attribution-props
            :state-evidence
            (dissoc :evidence-id :document-id)
            (assoc-in [:content]
                      paragraph-text)
            (assoc-in [:content-data]
                      {:type :document-paragraph
                       :document-paragraph-id document-paragraph-id
                       :html-ranges [(.-outerHTML temp-div)]
                       :page-range {:start (dec page)
                                    :end (dec page)}}))
        (vec selected-constructs)
        :paragraph
        callback]))))


(defn on-add-analysis-to-document-evidence
  [document-id evidence selected-constructs callback]
  (rf/dispatch
   [::add-analysis-to-document-evidence
    document-id
    evidence
    (mapv :construct-id selected-constructs)
    callback]))

(defn page-range->string
  [page-range]
  (if (= (:start page-range) (:end page-range))
    (str "Page " (inc (:start page-range)))
    (str "Page " (inc (:start page-range))
         " - " (inc (:end page-range)))))